/* Palette picker (sections/sbs-palette.liquid).
   Mood switching, + / − (5..max), once-per-artwork email gate (Klaviyo list), every download tracked, downloads.
   The download builder (sbs-palette-save.js) is only fetched when someone presses a download button. */
(() => {
  if (customElements.get("sbs-palette")) return;

  const MIN = 5;
  const parse = (str) =>
    (str || "").split(",").filter(Boolean).map((t) => ({
      hex: "#" + t.slice(0, 6),
      x: parseInt(t.slice(6, 9), 10) / 1000,
      y: parseInt(t.slice(9, 12), 10) / 1000,
    }));
  const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s.]{2,}$/;   // needs a 2+ letter ending, as Klaviyo does
  const store = {
    get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode: gate asks again next visit */ } },
  };

  class SbsPalette extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfgEl = this.querySelector("[data-sbp-config]");
      if (!cfgEl) return;
      this.cfg = JSON.parse(cfgEl.textContent);
      this.moods = this.cfg.moods.map((m) => ({ ...m, swatches: parse(m.sw) }));
      this.mood = this.moods.find((m) => m.id === this.cfg.first) || this.moods[0];
      this.n = Math.min(Math.max(this.cfg.count || MIN, MIN), this.cfg.max || 10);
      this.$ = (sel) => this.querySelector(sel);

      this.$("[data-sbp-mood]").addEventListener("change", (e) => {
        this.mood = this.moods.find((m) => m.id === e.target.value) || this.mood;
        this.render();
      });
      this.$("[data-sbp-minus]").addEventListener("click", () => { this.n = Math.max(MIN, this.n - 1); this.render(); });
      this.$("[data-sbp-plus]").addEventListener("click", () => { this.n = Math.min(this.cfg.max, this.n + 1); this.render(); });
      this.querySelectorAll("[data-sbp-save]").forEach((b) =>
        b.addEventListener("click", () => this.request(b.dataset.sbpSave === "all" ? this.moods : [this.mood], b)));
      const form = this.$("[data-sbp-gate-form]");
      if (form) form.addEventListener("submit", (e) => { e.preventDefault(); this.unlock(form); });
      this.render();
    }

    current() { return this.mood.swatches.slice(0, this.n); }

    render() {
      const sw = this.current();
      const art = this.$("[data-sbp-art]");
      const bars = this.$("[data-sbp-bars]");
      art.querySelectorAll(".sbp-dot").forEach((d) => d.remove());
      sw.forEach((s) => {
        const d = document.createElement("span");
        d.className = "sbp-dot";
        d.style.cssText = `left:${s.x * 100}%;top:${s.y * 100}%;background:${s.hex}`;
        art.appendChild(d);
      });
      bars.replaceChildren(...sw.map((s) => {
        const b = document.createElement("span");
        b.className = "sbp-bar";
        b.style.background = s.hex;
        return b;
      }));
      this.$("[data-sbp-count]").textContent = `${this.n} ${this.cfg.countLabel || "colours"}`;
      this.$("[data-sbp-minus]").disabled = this.n <= MIN;
      this.$("[data-sbp-plus]").disabled = this.n >= this.cfg.max;
    }

    /* ---------- email gate: once per ARTWORK ---------- */
    get unlockKey() { return "sbs-palette:" + this.cfg.key; }

    request(moods, button) {
      if (this.busy) return;   // a second tap while images are building would start a second build
      this.pending = { moods, button };
      const unlocked = store.get(this.unlockKey);
      if (!this.cfg.gate || unlocked) {
        // every download is recorded; already-unlocked visitors are never asked for their email again
        let consent = false; try { consent = !!JSON.parse(unlocked || "{}").consent; } catch (e) { /* old value */ }
        this.track(store.get("sbs-palette:email"), consent, false);
        return this.download();
      }
      const gate = this.$("[data-sbp-gate]");
      const input = gate.querySelector("input[type=email]");
      input.value = store.get("sbs-palette:email") || "";   // pre-filled on later artworks: one tap
      gate.hidden = false;
      input.focus();
    }

    unlock(form) {
      const email = form.email.value.trim();
      const err = this.$("[data-sbp-gate-error]");
      if (!EMAIL.test(email)) { err.textContent = "Please enter a valid email."; return; }
      err.textContent = "";
      const consent = !!(form.consent && form.consent.checked);
      this.track(email, consent, true);
      this.subscribe(email, consent);
      store.set("sbs-palette:email", email);
      store.set(this.unlockKey, JSON.stringify({ at: Date.now(), consent }));
      this.$("[data-sbp-gate]").hidden = true;
      this.download();
    }

    // "Downloaded Palette" event on EVERY download (first one via the email box, repeats silently)
    track(email, consent, first) {
      const k = this.cfg.klaviyo || {};
      const props = {
        Artwork: this.cfg.title,
        "Artwork URL": this.cfg.url,
        Moods: this.pending.moods.map((m) => m.label).join(", "),
        Colours: this.n,
        "Marketing consent": consent,
        "First download of artwork": first,
      };
      try {   // same pattern as the theme's quiz / "Send us your wall" sections
        if (window.klaviyo) {
          // Only tie the email to this browser's Klaviyo profile with consent. Without it the event still
          // reaches a profile Klaviyo already knows from its cookie, and is dropped for anonymous visitors.
          if (email && consent) window.klaviyo.push(["identify", { email }]);
          window.klaviyo.push(["track", k.event || "Downloaded Palette", props]);
        }
      } catch (e) { console.error("Palette: Klaviyo tracking failed", e); }
    }

    // only from the email box, and only with consent ticked + a list configured
    subscribe(email, consent) {
      const k = this.cfg.klaviyo || {};
      if (consent && k.list && k.company) {   // consented AND a list is configured: subscribe
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(k.company)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", revision: "2026-10-15" },   // Klaviyo API revision; each is supported for 2 years
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: "Palette picker",
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email,
                      properties: { "Palette artwork": this.cfg.title },
                      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },   // what the ticked box agrees to
                    },
                  },
                },
              },
              relationships: { list: { data: { type: "list", id: k.list } } },
            },
          }),
        })
          .then((res) => {   // fetch only rejects on network errors; a 4xx/5xx needs checking here
            if (!res.ok) return res.text().then((t) => console.error("Palette: Klaviyo subscribe failed", res.status, t));
          })
          .catch((e) => console.error("Palette: Klaviyo subscribe failed", e));
      }
    }

    /* ---------- downloads (builder loaded on demand) ---------- */
    async download() {
      const { moods, button } = this.pending;
      const status = this.$("[data-sbp-status]");
      this.busy = true;
      button.setAttribute("aria-busy", "true");
      status.textContent = "Preparing your palette…";
      try {
        const mod = await import(this.dataset.saveUrl);
        const files = await mod.buildFiles({
          img: `${this.cfg.img}&width=${this.cfg.cardWidth || 1200}`,
          title: this.cfg.title,
          card: this.cfg.card,
          host: this,
          moods: moods.map((m) => ({ label: m.label, swatches: m.swatches.slice(0, this.n) })),
        });
        const how = await mod.deliver(files, this.cfg.title).catch((e) => {
          // phones only open the share sheet straight after a tap; building 10 images can outlast that.
          // Offer a fresh tap instead of failing.
          if (e && e.name === "NotAllowedError") return this.offerSave(mod, files);
          throw e;
        });
        // the browser can still block or drop a download, so don't claim the files were saved
        if (how === "downloaded") status.textContent = files.length > 1 ? `Downloading ${files.length} images.` : "Downloading your image.";
        else if (how === "shared") status.textContent = "";
        // "offered": leave the "Save to your phone" button showing
      } catch (e) {
        if (e && e.name === "AbortError") { status.textContent = ""; }   // share sheet dismissed
        else if (window.Shopify && window.Shopify.designMode) {
          // The theme editor shows the store in a restricted frame that blocks saving; customers never see this.
          console.warn("Palette: download blocked in the theme editor", e);
          status.textContent = "Downloads don't work inside the theme editor. Use Preview (or the live page) to test them.";
        }
        else { console.error("Palette: download failed", e); status.textContent = "Sorry, the download didn't work. Please try again."; }
      } finally {
        this.busy = false;
        button.removeAttribute("aria-busy");
      }
    }

    offerSave(mod, files) {
      const status = this.$("[data-sbp-status]");
      const b = document.createElement("button");
      b.type = "button"; b.className = "sbp-btn"; b.textContent = files.length > 1 ? `Save ${files.length} images to your phone` : "Save the image to your phone";
      b.addEventListener("click", () => mod.deliver(files, this.cfg.title).then(() => { status.textContent = ""; }).catch(() => {}));
      status.replaceChildren(b);
      return "offered";
    }
  }

  customElements.define("sbs-palette", SbsPalette);
})();
